'use client'

import * as React from 'react'
import { ArrowRight, BadgeCheck, MapPin, Quote, ShieldCheck, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appSignupUrl } from '@/lib/widget/embed'

// Client preview for the Google Reviews channel tool. A visitor sees a
// sample reviews widget (star rating, reviewer name + city, verified
// badge) and toggles between three layouts (carousel, grid, badge),
// then routes into the builder (source=reviews) or signs up. Every
// review here is clearly labelled SAMPLE, no real attributed quotes are
// invented (content-integrity rule). The real widget pulls Google plus
// Trustpilot and Feefo reviews in-app.

type Layout = 'carousel' | 'grid' | 'badge'

// Representative sample reviews. NOT real customers, labelled "Sample"
// throughout the UI and never emitted as Review JSON-LD.
type SampleReview = {
  initials: string
  name: string
  city: string
  rating: number
  body: string
}

const SAMPLE_REVIEWS: SampleReview[] = [
  {
    initials: 'SR',
    name: 'Sample reviewer',
    city: 'Manchester, UK',
    rating: 5,
    body: 'Arrived early and the quality is better than I expected. The size guide was spot on, so no returns. Would order again.',
  },
  {
    initials: 'SR',
    name: 'Sample reviewer',
    city: 'Austin, US',
    rating: 5,
    body: 'Second order from this brand. Checkout was quick and the photos on the site matched what turned up. Easy five stars.',
  },
  {
    initials: 'SR',
    name: 'Sample reviewer',
    city: 'Dublin, IE',
    rating: 4,
    body: 'Lovely product and fast delivery. Took one star off only because I would have liked more colour options.',
  },
  {
    initials: 'SR',
    name: 'Sample reviewer',
    city: 'Sydney, AU',
    rating: 5,
    body: 'Customer support replied within the hour and sorted a sizing swap with no fuss. The kind of service that earns repeat orders.',
  },
]

const SAMPLE_RATING = 4.8
const SAMPLE_COUNT = 1240

const LAYOUTS: { value: Layout; label: string }[] = [
  { value: 'carousel', label: 'Carousel' },
  { value: 'grid', label: 'Grid' },
  { value: 'badge', label: 'Badge' },
]

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

function Stars({ rating, className }: { rating: number; className?: string }) {
  const full = Math.round(rating)
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)} aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn('h-3.5 w-3.5', i < full ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40')}
          aria-hidden
        />
      ))}
    </span>
  )
}

export function GoogleReviewsPreview() {
  const reducedMotion = usePrefersReducedMotion()
  const [layout, setLayout] = React.useState<Layout>('carousel')

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:items-start">
      {/* Preview */}
      <div className="order-1 rounded-2xl border bg-card p-4 sm:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
            <BadgeCheck className="h-4 w-4 text-primary" aria-hidden />
            Verified reviews
          </span>
          <span className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Sample
          </span>
        </div>

        <div className="rounded-xl border bg-background p-4" aria-live="polite">
          {layout === 'badge' ? (
            <BadgeLayout />
          ) : (
            <ul
              className={cn(
                layout === 'grid'
                  ? 'grid gap-3 sm:grid-cols-2'
                  : 'flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none]',
              )}
            >
              {SAMPLE_REVIEWS.map((r, i) => (
                <li
                  key={i}
                  className={cn(layout === 'carousel' && 'w-72 shrink-0 snap-start sm:w-80')}
                >
                  <ReviewCard review={r} reducedMotion={reducedMotion} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          Sample reviews shown for layout. The live widget pulls your real Google, Trustpilot and Feefo reviews, refreshed
          automatically.
        </p>
      </div>

      {/* Controls + CTA */}
      <div className="order-2 lg:sticky lg:top-24">
        <div className="rounded-2xl border bg-card p-5 sm:p-6">
          <span className="block font-display text-lg font-semibold tracking-tight text-foreground" id="gr-layout-label">
            Choose a layout
          </span>
          <p className="mt-1.5 text-sm text-muted-foreground">
            The preview updates instantly. Settle the shape here, then connect your real review sources when you publish.
          </p>

          <div
            className="mt-4 inline-flex w-fit rounded-full border bg-background p-0.5"
            role="group"
            aria-labelledby="gr-layout-label"
          >
            {LAYOUTS.map((l) => (
              <button
                key={l.value}
                type="button"
                onClick={() => setLayout(l.value)}
                aria-pressed={layout === l.value}
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  layout === l.value ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Pulls reviews from</span>
            <ul className="mt-2 flex flex-wrap gap-2">
              {['Google', 'Trustpilot', 'Feefo'].map((src) => (
                <li
                  key={src}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs font-medium text-foreground"
                >
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />
                  {src}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              href="/tools/widget-builder?source=reviews"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            >
              Build it now <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <a
              href={appSignupUrl({ source: 'reviews', ref: 'tool-google-reviews' })}
              className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium text-foreground transition-colors duration-150 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Connect reviews in app
            </a>
          </div>

          <p className="mt-5 inline-flex items-start gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
            Connect your Google Business profile in-app and only your verified reviews go live, kept fresh on a schedule.
          </p>
        </div>
      </div>
    </div>
  )
}

function ReviewCard({ review, reducedMotion }: { review: SampleReview; reducedMotion: boolean }) {
  return (
    <figure
      className={cn(
        'flex h-full flex-col rounded-xl border bg-card p-4',
        !reducedMotion && 'transition-colors duration-150 hover:border-primary/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <Stars rating={review.rating} />
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
          <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
          Verified
        </span>
      </div>
      <blockquote className="mt-2.5 flex-1 text-sm leading-relaxed text-foreground">
        <Quote className="mb-1 h-3.5 w-3.5 text-muted-foreground/50" aria-hidden />
        {review.body}
      </blockquote>
      <figcaption className="mt-3 flex items-center gap-2.5 border-t pt-3">
        <span
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-foreground"
          aria-hidden
        >
          {review.initials}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-foreground">{review.name}</span>
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" aria-hidden />
            {review.city}
          </span>
        </span>
      </figcaption>
    </figure>
  )
}

function BadgeLayout() {
  return (
    <div className="mx-auto flex max-w-xs flex-col items-center rounded-xl border bg-card p-6 text-center">
      <span className="text-3xl font-semibold tabular-nums text-foreground">{SAMPLE_RATING.toFixed(1)}</span>
      <Stars rating={SAMPLE_RATING} className="mt-1.5" />
      <p className="mt-2 text-sm text-muted-foreground">
        Based on{' '}
        <span className="font-medium text-foreground tabular-nums">{SAMPLE_COUNT.toLocaleString()}</span> verified reviews
      </p>
      <span className="mt-3 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Sample data
      </span>
    </div>
  )
}
